import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Typography,
  Container,
  Paper,
  InputBase,
  Link,
  CircularProgress,
} from "@mui/material";
import { createTheme, ThemeProvider, keyframes } from "@mui/material/styles";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { RequestUnlockOTP, VerifyUnlockOTP } from "../../config";
import {
  getPendingAuth,
  getPendingEmail,
  clearPendingAuth,
  commitJwtAuth,
  commitSsoAuth,
} from "./authStorage";

// ── Theme: clean, light and professional (matches the login page blues) ────
const C = {
  bg1: "#F7FAFD",
  bg2: "#E4EFF9",
  primary: "#0288D1",
  light: "#4FC3F7",
  deep: "#0277BD",
  text: "#1F2D3D",
  muted: "#5F6F81",
  border: "#CBD8E4",
  error: "#D32F2F",
};

// Seconds the "Send New Code" link stays locked after a successful send.
// Set to 0 to disable the cooldown.
const RESEND_COOLDOWN_SECONDS = 30;

const theme = createTheme({
  palette: {
    primary: { main: C.primary },
  },
});

// SweetAlert buttons match the brand blue
const themedSwal = Swal.mixin({
  confirmButtonColor: C.primary,
});

const reduceMotion = "@media (prefers-reduced-motion: reduce)";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  20%      { transform: translateX(-8px); }
  40%      { transform: translateX(8px); }
  60%      { transform: translateX(-5px); }
  80%      { transform: translateX(5px); }
`;

function maskEmail(email) {
  if (!email || !email.includes("@")) return email || "";
  const [local, domain] = email.split("@");
  const visible = local.slice(0, Math.min(2, local.length));
  return `${visible}${"•".repeat(Math.max(3, local.length - visible.length))}@${domain}`;
}

// Safely read the JSON body of a RequestUnlockOTP response (may be empty)
async function readJsonSafe(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

// Message for the "OTP already generated and still valid" response, e.g.
// { reused: true, validMinutes: 173, message: "An OTP was already ..." }
function reusedMessage(data) {
  if (data?.message) return data.message;
  const mins = data?.validMinutes;
  return mins
    ? `An OTP was already generated and is still valid for ${mins} more minute(s). Please check your email/SMS.`
    : "An OTP was already generated and is still valid. Please check your email/SMS.";
}

export default function OTPVerification({ onVerifySuccess }) {
  const [otp, setOtp] = React.useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = React.useState(false);
  const [resendLoading, setResendLoading] = React.useState(false);
  const [cooldown, setCooldown] = React.useState(0);
  const [shakeError, setShakeError] = React.useState(false);
  const [notice, setNotice] = React.useState(null); // { severity, text }
  const inputRefs = React.useRef([]);
  const autoSentRef = React.useRef(false);
  const navigate = useNavigate();
  const pending = getPendingAuth();
  const email = getPendingEmail(pending);

  React.useEffect(() => {
    if (!pending || !email) {
      themedSwal
        .fire({
          icon: "error",
          title: "Session Error",
          text: "User session not found. Please log in again.",
        })
        .then(() => navigate("/login"));
      return;
    }

    // Guard so the code is only requested once (e.g. React StrictMode in dev)
    if (autoSentRef.current) return;
    autoSentRef.current = true;

    // Automatically send OTP once when user arrives at this page
    const autoSendOtp = async () => {
      try {
        setResendLoading(true);
        const response = await fetch(RequestUnlockOTP.uri, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        });
        if (response.ok) {
          const data = await readJsonSafe(response);
          setOtp(["", "", "", "", "", ""]);
          inputRefs.current[0]?.focus();
          if (data?.reused) {
            // A valid code already exists: tell the user right away
            setNotice({ severity: "info", text: reusedMessage(data) });
          } else {
            setNotice(null);
            setCooldown(RESEND_COOLDOWN_SECONDS);
          }
        } else {
          themedSwal.fire({
            icon: "error",
            title: "Failed",
            text: "Unable to send OTP automatically. Try resending manually.",
          });
        }
      } catch {
        themedSwal.fire({
          icon: "error",
          title: "Network Error",
          text: "Could not send new code. Check your connection.",
        });
      } finally {
        setResendLoading(false);
      }
    };

    autoSendOtp();
  }, []);

  // Resend cooldown ticker
  React.useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  // Reset the shake flag after the animation finishes
  React.useEffect(() => {
    if (!shakeError) return undefined;
    const id = setTimeout(() => setShakeError(false), 600);
    return () => clearTimeout(id);
  }, [shakeError]);

  // ✅ Manual resend function (still available to user)
  const handleResend = async () => {
    if (cooldown > 0) return;
    setResendLoading(true);
    try {
      const response = await fetch(RequestUnlockOTP.uri, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (response.ok) {
        const data = await readJsonSafe(response);
        if (data?.reused) {
          // No new code was generated; keep what the user already typed
          setNotice({ severity: "info", text: reusedMessage(data) });
        } else {
          setNotice(null);
          themedSwal.fire({
            icon: "success",
            title: "Sent!",
            text: "A new code has been sent to your email.",
          });
          setOtp(["", "", "", "", "", ""]);
          inputRefs.current[0]?.focus();
          setCooldown(RESEND_COOLDOWN_SECONDS);
        }
      } else {
        themedSwal.fire({
          icon: "error",
          title: "Failed",
          text: "Could not send new code.",
        });
      }
    } catch {
      themedSwal.fire({
        icon: "error",
        title: "Failed",
        text: "Could not send new code.",
      });
    } finally {
      setResendLoading(false);
    }
  };

  const handleChange = (value, index) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) inputRefs.current[index + 1].focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1].focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      e.preventDefault();
      inputRefs.current[index + 1].focus();
    } else if (e.key === "Enter" && otp.join("").length === 6 && !loading) {
      handleSubmit();
    }
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (paste.length > 0) {
      const newOtp = paste
        .split("")
        .concat(Array(6).fill("").slice(paste.length));
      setOtp(newOtp.slice(0, 6));
      inputRefs.current[Math.min(paste.length - 1, 5)].focus();
    }
    e.preventDefault();
  };

  const handleBackToLogin = () => {
    // Nothing was committed to localStorage, so just drop the pending session
    clearPendingAuth();
    navigate("/login");
  };

  const handleSubmit = async () => {
    const unlockOTP = otp.join("");

    if (unlockOTP.length !== 6) {
      themedSwal.fire({ icon: "warning", title: "Invalid OTP", text: "Please enter all 6 digits." });
      return;
    }
    if (!pending) {
      themedSwal.fire({ icon: "error", title: "Session Expired", text: "Please log in again." });
      navigate("/login");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(VerifyUnlockOTP.uri, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, unlockOTP }),
      });

      if (!response.ok) {
        setShakeError(true);
        themedSwal.fire({ icon: "error", title: "Verification Failed", text: "Invalid or expired OTP." });
        return;
      }

      // Verified — this is the ONLY point where the real session is
      // written to localStorage.
      const decodedToken =
        pending.authType === "sso" ? commitSsoAuth(pending) : commitJwtAuth(pending);
      clearPendingAuth();

      onVerifySuccess && onVerifySuccess();

      if (pending.authType === "jwt" && pending.changepassword) {
        navigate("/changepassword");
        return;
      }

      const roleGroupId = parseInt(decodedToken?.RoleGroupId, 10);
      if (roleGroupId === 1) {
        navigate("/MCB-SWITCH");
      } else {
        navigate("/super-admin-dashboard");
      }
    } catch {
      setShakeError(true);
      themedSwal.fire({ icon: "error", title: "Unexpected Error", text: "Something went wrong. Try again later." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          position: "relative",
          overflow: "hidden",
          background: `linear-gradient(160deg, ${C.bg1} 0%, ${C.bg2} 100%)`,
        }}
      >
        {/* Soft, static background shapes */}
        <Box
          sx={{
            position: "absolute",
            top: -140,
            right: -120,
            width: 420,
            height: 420,
            borderRadius: "50%",
            background: "rgba(79, 195, 247, 0.16)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -180,
            left: -140,
            width: 480,
            height: 480,
            borderRadius: "50%",
            background: "rgba(2, 136, 209, 0.07)",
            pointerEvents: "none",
          }}
        />

        <Container
          component="main"
          maxWidth="sm"
          sx={{ position: "relative", zIndex: 2 }}
        >
          <Paper
            elevation={0}
            sx={{
              position: "relative",
              overflow: "hidden",
              maxWidth: 480,
              mx: "auto",
              p: { xs: 3, sm: 5 },
              borderRadius: 3,
              background: "#fff",
              border: "1px solid #E3ECF4",
              boxShadow: "0 12px 40px rgba(2, 60, 110, 0.10)",
              animation: `${fadeUp} 0.5s ease-out both`,
              [reduceMotion]: { animation: "none" },
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: `linear-gradient(90deg, ${C.light} 0%, ${C.primary} 100%)`,
              },
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                mx: "auto",
                mb: 2.5,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(2, 136, 209, 0.10)",
              }}
            >
              <MarkEmailReadOutlinedIcon sx={{ fontSize: 36, color: C.primary }} />
            </Box>

            <Typography
              component="h1"
              variant="h5"
              align="center"
              gutterBottom
              sx={{ color: C.deep, fontWeight: 700 }}
            >
              Confirm Your Identity
            </Typography>

            <Typography variant="body2" align="center" sx={{ color: C.muted }}>
              Enter the 6-digit verification code sent to
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
              <Box
                sx={{
                  px: 1.75,
                  py: 0.5,
                  borderRadius: 5,
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  color: C.deep,
                  background: "#EEF5FB",
                  border: "1px solid #D5E4F0",
                  wordBreak: "break-all",
                }}
              >
                {email ? maskEmail(email) : "loading..."}
              </Box>
            </Box>

            {/* Server notice, e.g. "OTP already generated and still valid" */}
            {notice && (
              <Alert
                severity={notice.severity}
                role="status"
                onClose={() => setNotice(null)}
                sx={{
                  mt: 3,
                  borderRadius: 2,
                  alignItems: "center",
                  textAlign: "left",
                  color: C.deep,
                  bgcolor: "#EAF5FD",
                  border: "1px solid #CFE6F7",
                  "& .MuiAlert-icon": { color: C.primary },
                }}
              >
                {notice.text}
              </Alert>
            )}

            {/* OTP boxes */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: { xs: 0.75, sm: 1.25 },
                mt: 4,
                mb: 3.5,
                animation: shakeError ? `${shake} 0.5s ease-in-out` : "none",
                [reduceMotion]: { animation: "none" },
              }}
            >
              {otp.map((digit, index) => (
                <InputBase
                  key={index}
                  inputRef={(el) => (inputRefs.current[index] = el)}
                  value={digit}
                  autoFocus={index === 0}
                  onChange={(e) => handleChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPaste={handlePaste}
                  inputProps={{
                    maxLength: 1,
                    inputMode: "numeric",
                    autoComplete: index === 0 ? "one-time-code" : "off",
                    "aria-label": `Digit ${index + 1} of 6`,
                    onFocus: (e) => e.target.select(),
                    style: {
                      textAlign: "center",
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      padding: 0,
                      height: "100%",
                    },
                  }}
                  sx={{
                    width: { xs: 42, sm: 52 },
                    height: { xs: 52, sm: 60 },
                    color: C.text,
                    border: "1.5px solid",
                    borderColor: shakeError
                      ? C.error
                      : digit
                      ? C.primary
                      : C.border,
                    borderRadius: 2,
                    bgcolor: digit ? "#F1F8FE" : "#fff",
                    transition: "all 0.2s",
                    "&.Mui-focused": {
                      borderColor: shakeError ? C.error : C.primary,
                      boxShadow: "0 0 0 3px rgba(2, 136, 209, 0.18)",
                    },
                  }}
                />
              ))}
            </Box>

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={loading || otp.join("").length !== 6}
              sx={{
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
                color: "#fff",
                background: `linear-gradient(135deg, ${C.light} 0%, ${C.primary} 100%)`,
                boxShadow: "0 4px 14px 0 rgba(79, 195, 247, 0.35)",
                "&:hover": {
                  background: "linear-gradient(135deg, #29B6F6 0%, #0277BD 100%)",
                  boxShadow: "0 6px 20px 0 rgba(79, 195, 247, 0.5)",
                },
                "&.Mui-disabled": {
                  color: "#9AA8B6",
                  background: "#E6ECF2",
                  boxShadow: "none",
                },
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={22} color="inherit" sx={{ mr: 1.5 }} />
                  Verifying...
                </>
              ) : (
                "Verify & Continue"
              )}
            </Button>

            <Box sx={{ textAlign: "center", mt: 3 }}>
              <Typography variant="body2" sx={{ color: C.muted, mb: 0.5 }}>
                Didn't get the code?
              </Typography>
              <Link
                component="button"
                variant="body2"
                onClick={handleResend}
                disabled={resendLoading || cooldown > 0}
                sx={{
                  color: resendLoading || cooldown > 0 ? C.muted : C.primary,
                  textDecoration: "none",
                  fontWeight: 600,
                  cursor: resendLoading || cooldown > 0 ? "default" : "pointer",
                  "&:hover": {
                    textDecoration:
                      resendLoading || cooldown > 0 ? "none" : "underline",
                  },
                }}
              >
                {resendLoading
                  ? "Sending..."
                  : cooldown > 0
                  ? `Resend code in ${cooldown}s`
                  : "Send New Code"}
              </Link>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Button
                size="small"
                startIcon={<ArrowBackIcon fontSize="small" />}
                onClick={handleBackToLogin}
                sx={{
                  textTransform: "none",
                  color: C.muted,
                  "&:hover": { color: C.deep, background: "rgba(2,136,209,0.06)" },
                }}
              >
                Back to sign in
              </Button>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
                mt: 3,
                pt: 2,
                borderTop: "1px solid #E9EFF5",
                color: C.muted,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 15 }} />
              <Typography variant="caption">
                Never share this code with anyone.
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
}